/*
  ml-annual-upgrade — the monthly → annual upgrade page (design_handoff_annual_upgrade §4, §10, §11).

  STATE MACHINE (root data-state; data-view picks offer / success / annual / guest)
    default ─tap─▶ confirm ─Not now / Esc / backdrop─▶ default
                           └─pay─▶ processing ─▶ checkout redirect          (checkout path)
                                              ├▶ success                     (one-click: server says active)
                                              ├▶ error (declined / generic)  (nothing charged)
                                              └▶ pending (20 s, never re-enables pay on that key)

  TWO PATHS
  - Checkout (the default, and the only path while billing changes are on hold): add the annual
    plan (+ the free product for U3, with the annual-free-offer-selector's properties and cart
    attributes) → MlMembershipQty.normalize() → MlCheckoutGuard.check('upgrade') → /checkout
    (U3: /discount/CODE?redirect=/checkout). Attribution rides on monthlees-attribution.js's fetch
    wrapper, which re-syncs cart attributes after every /cart/add.
  - One-click: only when the section's "One-click upgrade (requires backend)" is on AND an endpoint
    is set AND the member is monthly AND GET <endpoint>/quote answers { eligible: true }. Contract
    (the backend owns every charge; the theme holds no secret):
      GET  <endpoint>/quote   → { eligible, charge_today, card_brand?, card_last4?, renews_on?, credit? }
      POST <endpoint>         { concept, gift_handle?, idempotency_key }
           200 { status: "active", charge: {amount, currency}, renews_on, gift? } → success
           202 { status: "pending", poll } → poll every 1.5 s up to 20 s
           402 { status: "declined" } · 409 { status: "ineligible", reason } · 5xx → error
      GET  <endpoint>/status?key=… → same shapes; 404 = the server never started it.
    Success is shown ONLY for status "active" with a charge. A new idempotency key per confirm-open;
    the pay button is disabled on first press.

  PREVIEWS (unpublished themes / Theme Editor only, and "Allow previews" on): ?state=confirm|
  processing|error|pending|success, ?as=monthly|annual|guest|unknown, ?mode=oneclick. A ribbon says
  it's a preview and that nothing was charged; a previewed one-click pay never sends anything.

  Analytics: ml_upgrade_view / tap / confirm / success / decline / pending / fallback_checkout /
  dismiss through MonthleesAnalytics.track (PostHog). Never card or email data.
*/
(function () {
  'use strict';
  if (window.MlAnnualUpgrade) return;

  var POLL_MS = 1500;
  var POLL_MAX_MS = 20000;
  var CHECKOUT_TIMEOUT_MS = 15000;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var COPY = {
    busyOneClick: 'Switching you to annual…',
    busyCheckout: 'Taking you to secure checkout…',
    payCheckout: 'Continue to secure checkout',
    declinedH: 'Your card on file was declined, so nothing was charged.',
    declinedD: 'You’re still on monthly. Update your card, or switch through secure checkout instead.',
    genericH: 'Something went wrong on our side, so nothing was charged.',
    genericD: 'You’re still on monthly. You can switch through secure checkout instead.',
    cartH: 'That didn’t go through. Try again.',
  };

  function track(name, props) {
    try {
      if (window.MonthleesAnalytics && typeof window.MonthleesAnalytics.track === 'function') {
        window.MonthleesAnalytics.track(name, props || {});
      } else if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture(name, props || {});
      }
    } catch (e) { /* never break the page */ }
  }

  function uuid() {
    try { if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID(); } catch (e) {}
    var b = new Uint8Array(16);
    (window.crypto || window.msCrypto).getRandomValues(b);
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    var h = Array.prototype.map.call(b, function (x) { return ('0' + x.toString(16)).slice(-2); }).join('');
    return h.slice(0, 8) + '-' + h.slice(8, 12) + '-' + h.slice(12, 16) + '-' + h.slice(16, 20) + '-' + h.slice(20);
  }

  // Fill {tokens}; a token with no value blanks the whole line (never guess a figure).
  function fill(tpl, map) {
    if (!tpl) return '';
    var missing = false;
    var out = String(tpl).replace(/\{([a-z_]+)\}/g, function (m, k) {
      var v = map[k];
      if (v === undefined || v === null || v === '') { missing = true; return ''; }
      return v;
    });
    return missing ? '' : out;
  }

  function money(v) {
    if (v === undefined || v === null || v === '') return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'object' && v.amount !== undefined) {
      var n = Number(v.amount);
      if (!isFinite(n)) return '';
      try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: v.currency || 'USD' }).format(n); } catch (e) { return '$' + n.toFixed(2); }
    }
    return '';
  }

  function dateText(v) {
    if (!v) return '';
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
    if (!m) return String(v);
    var d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function source() {
    try {
      var p = new URLSearchParams(window.location.search);
      if (p.get('utm_source')) return p.get('utm_source');
      var rec = JSON.parse(window.localStorage.getItem('monthlees_attr_v1') || 'null') || {};
      return rec.utm_source || rec.last_utm_source || rec.first_utm_source || rec.source || 'direct';
    } catch (e) { return 'direct'; }
  }

  function withTimeout(promise, ms) {
    return Promise.race([promise, new Promise(function (_, reject) { setTimeout(function () { reject(new Error('timeout')); }, ms); })]);
  }

  function init(root) {
    if (!root || root.getAttribute('data-ml-au-ready') === 'true') return;
    root.setAttribute('data-ml-au-ready', 'true');
    var page = root.closest('[data-ml-au-page]') || root.parentNode;
    var dataEl = page.querySelector('[data-ml-au-data]');
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }

    function $(sel) { return root.querySelector(sel); }
    function $$(sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

    var params = new URLSearchParams(window.location.search);
    var role = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
    var previewAllowed = root.getAttribute('data-allow-preview') === 'true' &&
      (root.getAttribute('data-design-mode') === 'true' || (!!role && role !== 'main'));

    var st = {
      concept: data.concept || 'direct',
      member: data.member || 'guest',
      billing: data.billing || '',
      mode: 'checkout',
      quote: null,
      key: null,
      busy: false,
      preview: '',
      previewOneClick: false,
      lastFocus: null,
      startedAt: 0,
    };

    var offer = params.get('offer');
    if (root.getAttribute('data-offer-param') === 'true' && /^(direct|ledger|gift)$/.test(offer || '')) st.concept = offer;
    if (previewAllowed) {
      var as = params.get('as');
      if (/^(monthly|legacy|annual|guest|unknown|plus|nonmember)$/.test(as || '')) {
        st.member = as === 'legacy' ? 'monthly' : as;
        st.billing = as === 'legacy' ? 'native' : as === 'monthly' ? 'bold' : '';
      }
      if (params.get('mode') === 'oneclick' && st.billing !== 'native') { st.mode = 'oneclick'; st.previewOneClick = true; }
      var ps = params.get('state');
      if (/^(confirm|processing|error|pending|success)$/.test(ps || '')) st.preview = ps;
    }

    var cta = $('[data-ml-au-cta]');
    var ctaLabel = $('[data-ml-au-cta-label]');
    var cf = $('[data-ml-au-cf]');
    var pay = $('[data-ml-au-pay]');
    var payLabel = $('[data-ml-au-pay-label]');
    var statusEl = $('[data-ml-au-status]');
    var errEl = $('[data-ml-au-error]');
    var pendingEl = $('[data-ml-au-pending]');

    function plan() { return (data.plans && data.plans[st.concept]) || {}; }
    function selectedGift() {
      var block = $('[data-concept-block="gift"]');
      var r = block ? block.querySelector('.ml-au-gift__radio:checked') : null;
      return r ? { handle: r.value, variant: r.getAttribute('data-variant'), title: r.getAttribute('data-title'), img: r.getAttribute('data-img'), bg: r.getAttribute('data-bg') } : null;
    }
    function savingText() {
      var block = $('[data-concept-block="' + st.concept + '"]');
      return block ? block.getAttribute('data-saving') || '' : '';
    }
    function chargeToday() {
      if (st.quote && st.quote.charge_today) return money(st.quote.charge_today);
      return plan().money || '';
    }

    /* ---- background (U3 follows the selected gift, cross-faded) ---- */
    var bgs = $$('[data-ml-au-bg]');
    function setBg(src) {
      if (!src || bgs.length < 2) return;
      var on = bgs[0].classList.contains('is-on') ? bgs[0] : bgs[1];
      var off = on === bgs[0] ? bgs[1] : bgs[0];
      if (on.getAttribute('src') === src) return;
      var swap = function () { off.classList.add('is-on'); on.classList.remove('is-on'); };
      off.onload = swap;
      off.setAttribute('src', src);
      if (off.complete && off.naturalWidth) swap();
    }
    function syncBg() {
      if (st.concept === 'gift') {
        var g = selectedGift();
        if (g && g.bg) return setBg(g.bg);
      }
      setBg(bgs[0] && bgs[0].getAttribute('data-default-src'));
    }

    /* ---- render the current concept / member / mode ---- */
    function apply() {
      root.setAttribute('data-concept', st.concept);
      root.setAttribute('data-member', st.member);
      root.setAttribute('data-billing', st.billing);
      root.setAttribute('data-mode', st.mode);
      var view = (st.member === 'guest' || st.member === 'nonmember') ? 'guest' : st.member === 'annual' ? 'annual' : st.member === 'plus' ? 'plus' : 'offer';
      if (root.getAttribute('data-view') !== 'success') root.setAttribute('data-view', view);
      root.setAttribute('data-plan-ok', plan().ok ? 'true' : 'false');

      var lbl = st.concept === 'gift' ? data.copy.ctaGift : fill(data.copy.ctaDirect, { annual: plan().money });
      if (ctaLabel && !st.busy) ctaLabel.textContent = lbl || 'Switch to annual';

      var terms = $('[data-ml-au-terms]');
      if (terms) {
        var tpl = st.mode === 'oneclick'
          ? (st.concept === 'gift' ? data.copy.termsGift : data.copy.terms)
          : (st.concept === 'gift' ? data.copy.termsGiftCheckout : data.copy.termsCheckout);
        terms.textContent = fill(tpl, { charge_today: chargeToday(), annual: plan().money });
      }

      // The card line only when the endpoint told us the card (never guessed; hidden = confirm is mandatory anyway).
      var card = $('[data-ml-au-card]');
      if (card) {
        var q = st.quote;
        var show = st.mode === 'oneclick' && q && q.card_last4;
        card.hidden = !show;
        if (show) {
          $('[data-ml-au-card-brand]').textContent = q.card_brand || 'Card';
          $('[data-ml-au-card-text]').textContent = 'Charged to the card on file ending ' + q.card_last4;
        }
      }

      var who = $('[data-ml-au-who-plan]');
      if (who) who.textContent = st.member === 'monthly' ? ' · MONTHLY MEMBER' : st.member === 'annual' ? ' · ANNUAL MEMBER' : st.member === 'plus' ? ' · PLUS MEMBER' : st.member === 'unknown' ? ' · MEMBER' : '';
      // Signed in but not a member: no sign-in button, the non-member heading.
      var gh = $('[data-ml-au-guest-h]');
      if (gh && st.member === 'nonmember') {
        gh.textContent = gh.getAttribute('data-nonmember') || gh.textContent;
        var gs = $('[data-ml-au-guest-sub]'); if (gs) gs.hidden = true;
        var li = $('[data-ml-au-login]'); if (li) li.hidden = true;
      }

      var login = $('[data-ml-au-login]');
      if (login) login.setAttribute('href', '/customer_authentication/login?return_to=' + encodeURIComponent(window.location.pathname + window.location.search));
      var join = $('[data-ml-au-join]');
      if (join) {
        var utm = [];
        params.forEach(function (v, k) { if (/^utm_/.test(k)) utm.push(encodeURIComponent(k) + '=' + encodeURIComponent(v)); });
        if (utm.length) {
          var base = join.getAttribute('href').split('?')[0];
          join.setAttribute('href', base + '?' + utm.join('&'));
        }
      }
      // An eyebrow that says MONTHLY is wrong for a member whose plan the store can't tell.
      if (st.member === 'unknown' && data.copy.eyebrowUnknown) {
        $$('.ml-au__concept .ml-au__eyebrow').forEach(function (e) { if (/MONTHLY/.test(e.textContent)) e.textContent = data.copy.eyebrowUnknown; });
      }
      root.setAttribute('data-heading-id', root.id + '-h-' + st.concept);
      syncBg();
    }

    /* ---- confirm sheet ---- */
    function focusables() {
      return Array.prototype.slice.call(cf.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])'))
        .filter(function (el) { return !el.hidden && el.offsetParent !== null; });
    }
    function onKey(e) {
      if (cf.hidden) return;
      if (e.key === 'Escape') { e.preventDefault(); closeConfirm(true); return; }
      if (e.key !== 'Tab') return;
      var f = focusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    function fillConfirm() {
      var oneClick = st.mode === 'oneclick';
      var charge = oneClick ? chargeToday() : plan().money;
      $('[data-ml-au-cf-charge-k]').textContent = oneClick ? 'Charged today' : 'Due at checkout';
      $('[data-ml-au-cf-charge]').textContent = charge;
      var q = st.quote || {};
      var cardRow = $('[data-ml-au-cf-card-row]');
      cardRow.hidden = !(oneClick && q.card_last4);
      if (!cardRow.hidden) $('[data-ml-au-cf-card]').textContent = (q.card_brand || 'Card') + ' ending ' + q.card_last4;
      var giftRow = $('[data-ml-au-cf-gift-row]');
      var g = st.concept === 'gift' ? selectedGift() : null;
      giftRow.hidden = !g;
      if (g) $('[data-ml-au-cf-gift]').textContent = g.title;
      $('[data-ml-au-cf-renews]').textContent = oneClick && q.renews_on ? dateText(q.renews_on) : 'Every year';
      var monthlyRow = $('[data-ml-au-cf-monthly-row]');
      if (monthlyRow) {
        var note = st.billing === 'native' ? data.monthlyNoteLegacy : data.monthlyNote;
        if (note) monthlyRow.querySelector('dd').textContent = note;
        monthlyRow.hidden = oneClick || !note;
      }
      // §8: a credit for the month already paid, only when the server says there is one.
      var credit = $('[data-ml-au-cf-credit-row]');
      if (credit) credit.parentNode.removeChild(credit);
      if (oneClick && q.credit) {
        var row = document.createElement('div');
        row.className = 'ml-au-cf__row';
        row.setAttribute('data-ml-au-cf-credit-row', '');
        row.innerHTML = '<dt>Credit for this month</dt><dd></dd>';
        row.querySelector('dd').textContent = '−' + money(q.credit).replace(/^-/, '');
        var chargeRow = $('[data-ml-au-cf-charge-row]');
        chargeRow.parentNode.insertBefore(row, chargeRow);
      }
      payLabel.textContent = oneClick ? (charge ? 'Pay ' + charge + ' and switch' : 'Pay and switch') : COPY.payCheckout;
      pay.removeAttribute('aria-disabled');
    }

    function openConfirm(isPreview) {
      if (st.busy || root.getAttribute('data-view') !== 'offer' || !plan().ok) return;
      if (!isPreview) track('ml_upgrade_tap', { concept: st.concept, gift_handle: st.concept === 'gift' && selectedGift() ? selectedGift().handle : undefined });
      hideError();
      st.key = uuid();
      fillConfirm();
      st.lastFocus = document.activeElement && document.activeElement !== document.body ? document.activeElement : cta;
      cf.hidden = false;
      root.setAttribute('data-state', 'confirm');
      document.addEventListener('keydown', onKey, true);
      try { pay.focus({ preventScroll: true }); } catch (e) { pay.focus(); }
    }

    function closeConfirm(dismissed) {
      if (cf.hidden) return;
      cf.hidden = true;
      document.removeEventListener('keydown', onKey, true);
      if (dismissed) {
        root.setAttribute('data-state', 'default');
        track('ml_upgrade_dismiss', { concept: st.concept, at: 'confirm' });
        var back = st.lastFocus || cta;
        try { back.focus({ preventScroll: true }); } catch (e) { back.focus(); }
      }
    }

    /* ---- busy / error / pending ---- */
    function setBusy(label) {
      st.busy = true;
      root.setAttribute('data-state', 'processing');
      cta.setAttribute('aria-disabled', 'true');
      cta.setAttribute('aria-busy', 'true');
      ctaLabel.textContent = label;
      if (statusEl) statusEl.textContent = label;
    }
    function clearBusy() {
      st.busy = false;
      cta.removeAttribute('aria-disabled');
      cta.removeAttribute('aria-busy');
      if (statusEl) statusEl.textContent = '';
      root.setAttribute('data-state', 'default');
      apply();
    }
    function hideError() { if (errEl) errEl.hidden = true; }
    function showError(kind) {
      clearBusy();
      st.key = null; // a new key on the next confirm
      root.setAttribute('data-state', 'error');
      var h = kind === 'declined' ? COPY.declinedH : kind === 'cart' ? COPY.cartH : COPY.genericH;
      var d = kind === 'declined' ? COPY.declinedD : kind === 'cart' ? '' : COPY.genericD;
      $('[data-ml-au-error-h]').textContent = h;
      $('[data-ml-au-error-d]').textContent = d;
      $('[data-ml-au-error-actions]').hidden = kind === 'cart';
      $('[data-ml-au-update-card]').hidden = kind !== 'declined';
      errEl.hidden = false;
    }
    function showPending() {
      // Never re-enable pay on the same key: the button stays disabled.
      st.busy = true;
      root.setAttribute('data-state', 'pending');
      cta.setAttribute('aria-disabled', 'true');
      cta.removeAttribute('aria-busy');
      ctaLabel.textContent = COPY.busyOneClick;
      if (statusEl) statusEl.textContent = '';
      pendingEl.hidden = false;
      track('ml_upgrade_pending', { concept: st.concept });
    }

    /* ---- success (only on a confirmed "active", or a labelled preview) ---- */
    function showSuccess(v) {
      closeConfirm(false);
      st.busy = false;
      hideError();
      pendingEl.hidden = true;
      root.setAttribute('data-state', 'default');
      root.setAttribute('data-view', 'success');
      var saving = savingText();
      var subEl = $('[data-ml-au-ok-sub]');
      var subTpl = st.concept === 'gift' ? subEl.getAttribute('data-sub-gift') : subEl.getAttribute('data-sub-direct');
      subEl.textContent = fill(subTpl, { saving: saving });
      $('[data-ml-au-ok-saving]').textContent = saving;
      $('[data-ml-au-fact-save]').hidden = !saving;
      var renews = dateText(v.renews_on);
      $('[data-ml-au-ok-renews]').textContent = renews;
      $('[data-ml-au-fact-renews]').hidden = !renews;
      var giftCard = $('[data-ml-au-ok-gift]');
      var g = st.concept === 'gift' ? (v.gift || selectedGift()) : null;
      giftCard.hidden = !g;
      if (g) {
        $('[data-ml-au-ok-gift-n]').textContent = g.title || '';
        var img = $('[data-ml-au-ok-gift-img]');
        if (g.img || g.image) img.setAttribute('src', g.img || g.image); else img.hidden = true;
      }
      var foot = $('[data-ml-au-ok-foot]');
      var parts = [];
      var email = foot.getAttribute('data-email');
      if (v.receipt && email) parts.push('RECEIPT SENT TO ' + email);
      if (renews) parts.push('RENEWS ' + renews.toUpperCase());
      parts.push('CANCEL ANYTIME');
      foot.textContent = parts.join(' · ');
      syncBg();
      var h = $('[data-ml-au-success] .ml-au-ok__h');
      if (h) { try { h.focus({ preventScroll: true }); } catch (e) { h.focus(); } }
      window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    /* ---- checkout path (handoff §10) ---- */
    function runCheckout(trigger) {
      var p = plan();
      if (!p.ok || !p.variant || !p.plan) { showError('cart'); return; }
      setBusy(COPY.busyCheckout);
      var mine = st.startedAt = Date.now();
      var timer = setTimeout(function () { if (st.startedAt === mine && st.busy) showError('cart'); }, CHECKOUT_TIMEOUT_MS);
      var from = st.member === 'monthly' ? 'monthly' : st.member;
      var billing = st.billing || 'unknown';
      var items, attrs, g = null;
      if (st.concept === 'gift') {
        g = selectedGift();
        if (!g || !g.variant) { clearTimeout(timer); showError('cart'); return; }
        items = [
          { id: Number(p.variant), quantity: 1, selling_plan: Number(p.plan), properties: {
            _offer_campaign: data.gift.campaign, _offer_type: 'annual_free_product',
            _requested_customer_tag: data.gift.customerTag || '', _upgrade_from: from, _upgrade_billing: billing } },
          { id: Number(g.variant), quantity: 1, properties: {
            _free_offer_item: 'true', _offer_campaign: data.gift.campaign, _offer_option: g.title || '',
            _offer_option_tag: data.gift.customerTag || '', _product_handle: g.handle } }
        ];
        attrs = { offer_campaign: data.gift.campaign, offer_type: 'annual_free_product', offer_option: g.title || '',
          customer_tag_request: data.gift.customerTag || '', free_shipping_offer: data.gift.freeShippingMarker || '', upgrade_from: from, upgrade_billing: billing };
      } else {
        items = [{ id: Number(p.variant), quantity: 1, selling_plan: Number(p.plan), properties: {
          _offer_campaign: data.upgradeCampaign, _offer_type: 'annual_upgrade', _upgrade_from: from, _upgrade_billing: billing } }];
        attrs = { offer_campaign: data.upgradeCampaign, offer_type: 'annual_upgrade', upgrade_from: from, upgrade_billing: billing };
      }
      track('ml_upgrade_fallback_checkout', { concept: st.concept, trigger: trigger, gift_handle: g ? g.handle : undefined });
      var root_ = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      window.fetch(root_ + 'cart/add.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: items })
      })
        .then(function (res) {
          return res.json().catch(function () { return {}; }).then(function (d) {
            if (!res.ok) throw new Error((d && (d.description || d.message)) || ('HTTP ' + res.status));
            return d;
          });
        })
        .then(function () {
          return window.fetch(root_ + 'cart/update.js', {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: attrs })
          }).catch(function () {});
        })
        .then(function () {
          document.dispatchEvent(new CustomEvent('monthlees:cart-updated', { detail: { source: 'ml-annual-upgrade' } }));
          return window.MlMembershipQty && typeof window.MlMembershipQty.normalize === 'function' ? window.MlMembershipQty.normalize() : null;
        })
        .then(function () {
          return window.MlCheckoutGuard && typeof window.MlCheckoutGuard.check === 'function' ? window.MlCheckoutGuard.check('upgrade') : true;
        })
        .then(function (ok) {
          clearTimeout(timer);
          if (st.startedAt !== mine || !st.busy) return;
          if (ok === false) { clearBusy(); return; }
          var code = st.concept === 'gift' ? (data.gift.discount || '').trim() : '';
          window.location.assign(code ? root_ + 'discount/' + encodeURIComponent(code) + '?redirect=/checkout' : root_ + 'checkout');
        })
        .catch(function (err) {
          clearTimeout(timer);
          track('ml_upgrade_error', { concept: st.concept, path: 'checkout', reason: String((err && err.message) || err).slice(0, 200) });
          if (st.startedAt === mine) showError('cart');
        });
    }

    /* ---- one-click path (only with a real endpoint; the server charges, never this file) ---- */
    var endpoint = root.getAttribute('data-endpoint') || '';

    function handleResult(res, body, key) {
      var s = body && body.status;
      if (res.status === 200 && s === 'active' && body.charge) {
        track('ml_upgrade_success', { concept: st.concept, gift_handle: st.concept === 'gift' && selectedGift() ? selectedGift().handle : undefined, path: 'one_tap', latency_ms: Date.now() - st.startedAt });
        try {
          document.body.setAttribute('data-monthlees-member-plan', 'annual');
          document.dispatchEvent(new CustomEvent('ml:membership-changed', { detail: { plan: 'annual' } }));
        } catch (e) {}
        showSuccess({ renews_on: body.renews_on, gift: body.gift, receipt: body.receipt_sent !== false });
        return 'done';
      }
      if (res.status === 202 || s === 'pending') return 'pending';
      if (res.status === 402 || s === 'declined') {
        track('ml_upgrade_decline', { concept: st.concept, reason: body && body.reason ? String(body.reason).slice(0, 60) : 'declined' });
        showError('declined');
        return 'done';
      }
      if (res.status === 409 && body && body.reason === 'already_annual') {
        st.busy = false;
        st.member = 'annual';
        root.setAttribute('data-view', 'annual');
        apply();
        return 'done';
      }
      if (res.status === 404) { showError('generic'); return 'done'; }
      if (res.status >= 400) { showError('generic'); return 'done'; }
      return 'pending';
    }

    function poll(url, key) {
      if (Date.now() - st.startedAt >= POLL_MAX_MS) { showPending(); return; }
      setTimeout(function () {
        if (st.key !== key) return;
        window.fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' })
          .then(function (res) { return res.json().catch(function () { return {}; }).then(function (b) { return handleResult(res, b, key); }); })
          .catch(function () { return 'pending'; })
          .then(function (r) { if (r === 'pending' && st.key === key) poll(url, key); });
      }, POLL_MS);
    }

    function runOneClick() {
      var key = st.key;
      var g = st.concept === 'gift' ? selectedGift() : null;
      track('ml_upgrade_confirm', { concept: st.concept, gift_handle: g ? g.handle : undefined, charge_amount: chargeToday() });
      setBusy(COPY.busyOneClick);
      st.startedAt = Date.now();
      if (st.previewOneClick && !endpoint) {
        // Preview only: nothing is sent anywhere. The ribbon says so.
        setTimeout(function () { showSuccess({ renews_on: previewRenews(), receipt: true }); }, 1200);
        return;
      }
      var statusUrl = endpoint.replace(/\/$/, '') + '/status?key=' + encodeURIComponent(key);
      window.fetch(endpoint, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ concept: st.concept, gift_handle: g ? g.handle : undefined, idempotency_key: key })
      })
        .then(function (res) {
          return res.json().catch(function () { return {}; }).then(function (b) {
            var r = handleResult(res, b, key);
            if (r === 'pending') poll(b && b.poll ? b.poll : statusUrl, key);
          });
        })
        .catch(function () {
          // We can't tell whether the server got it: ask the status endpoint rather than claim anything.
          poll(statusUrl, key);
        });
    }

    function loadQuote() {
      // One-click can only ever apply to Bold monthly members (legacy native contracts are out of reach).
      if (!data.oneclick || !endpoint || st.member !== 'monthly' || st.billing !== 'bold' || st.previewOneClick) return;
      root.setAttribute('data-mode', 'oneclick-pending');
      withTimeout(window.fetch(endpoint.replace(/\/$/, '') + '/quote', { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' })
        .then(function (r) { if (!r.ok) throw new Error('quote ' + r.status); return r.json(); }), 6000)
        .then(function (q) {
          if (q && q.eligible === true && q.charge_today) { st.quote = q; st.mode = 'oneclick'; }
          else st.mode = 'checkout';
          apply();
        })
        .catch(function () { st.mode = 'checkout'; apply(); });
    }

    function previewRenews() {
      var d = new Date();
      d.setFullYear(d.getFullYear() + 1);
      return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
    }

    /* ---- wiring ---- */
    cta.addEventListener('click', function (e) {
      e.preventDefault();
      if (st.busy || cta.getAttribute('aria-disabled') === 'true') return;
      openConfirm(false);
    });
    pay.addEventListener('click', function (e) {
      e.preventDefault();
      if (st.busy || pay.getAttribute('aria-disabled') === 'true') return; // one press only
      pay.setAttribute('aria-disabled', 'true');
      closeConfirm(false);
      if (st.mode === 'oneclick') runOneClick();
      else runCheckout(st.previewOneClick ? 'preview' : 'outcome_b');
    });
    $$('[data-ml-au-cf-close]').forEach(function (el) { el.addEventListener('click', function () { closeConfirm(true); }); });
    var fallback = $('[data-ml-au-fallback]');
    if (fallback) fallback.addEventListener('click', function () {
      hideError();
      root.setAttribute('data-state', 'default');
      st.mode = 'checkout';
      apply();
      runCheckout(st.preview === 'error' ? 'preview' : 'decline');
    });

    // Gift picker: native radios (arrow keys move the choice), the card and background follow.
    $$('.ml-au-gift__radio').forEach(function (r) {
      r.addEventListener('change', function () {
        $$('.ml-au-gift__opt').forEach(function (o) { o.classList.toggle('is-on', !!o.querySelector('.ml-au-gift__radio:checked')); });
        syncBg();
      });
    });
    var more = $('[data-ml-au-gift-toggle]');
    if (more) {
      var closedLabel = more.textContent;
      more.addEventListener('click', function () {
        var open = more.getAttribute('aria-expanded') === 'true';
        $$('[data-ml-au-gift-more]').forEach(function (el) { el.hidden = open; });
        more.setAttribute('aria-expanded', open ? 'false' : 'true');
        more.textContent = open ? closedLabel : 'Show fewer';
      });
    }

    var close = $('[data-ml-au-close]');
    if (close) close.addEventListener('click', function (e) {
      track('ml_upgrade_dismiss', { concept: st.concept, at: 'page' });
      var same = false;
      try { same = !!document.referrer && new URL(document.referrer).origin === window.location.origin; } catch (err) {}
      if (same && window.history.length > 1) { e.preventDefault(); window.history.back(); }
    });

    // Back from checkout (bfcache) must not leave the busy state on.
    window.addEventListener('pageshow', function (e) { if (e.persisted && st.busy && st.mode === 'checkout') { st.startedAt = 0; clearBusy(); } });

    /* ---- go ---- */
    apply();
    loadQuote();
    track('ml_upgrade_view', { concept: st.concept, source: source(), eligible: st.member === 'monthly' || st.member === 'unknown', member_case: st.member + (st.billing ? '_' + st.billing : '') });

    if (st.preview || params.get('as') && previewAllowed || st.previewOneClick) {
      var rib = $('[data-ml-au-preview]');
      if (rib) {
        rib.hidden = false;
        rib.textContent = 'PREVIEW · ' + (st.preview || 'offer').toUpperCase() + ' · AS ' + st.member.toUpperCase() + (st.billing ? ' (' + st.billing.toUpperCase() + ')' : '') +
          (st.mode === 'oneclick' ? ' · ONE-CLICK' : ' · CHECKOUT') + ' · NOTHING IS CHARGED';
      }
      switch (st.preview) {
        case 'confirm': openConfirm(true); break;
        case 'processing': setBusy(st.mode === 'oneclick' ? COPY.busyOneClick : COPY.busyCheckout); break;
        case 'error': showError('declined'); break;
        case 'pending': st.startedAt = Date.now(); showPending(); break;
        case 'success': showSuccess({ renews_on: previewRenews(), receipt: true }); break;
      }
    }
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-ml-au]'), init);
  }
  window.MlAnnualUpgrade = { init: init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target && e.target.querySelector ? e.target.querySelector('[data-ml-au]') : null;
    if (el) init(el);
  });
})();
